'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { HeroAppMockup } from './hero-app-mockup';

export function HeroSection() {
  const t = useTranslations('public.hero');
  return (
    <section className="relative overflow-hidden bg-[var(--eifo-bg-light)]">
      {/* soft decorative color washes for depth */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-eifo-accent/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 right-1/3 h-80 w-80 rounded-full bg-eifo-orange/10 blur-3xl"
      />

      {/* items-end so the student photo anchors to the bottom edge (the "split"
          with the next section) rather than floating mid-air. */}
      <div className="relative mx-auto grid max-w-7xl items-end gap-10 px-4 lg:grid-cols-2">
        {/* Copy — owns the vertical padding so the photo column can run flush
            to the bottom of the section. */}
        <div className="animate-in fade-in slide-in-from-bottom-4 py-16 text-center duration-700 md:py-24 lg:text-left">
          <span className="mb-4 inline-block rounded-full bg-eifo-orange/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-eifo-orange">
            {t('badge')}
          </span>
          <h1 className="font-display text-4xl font-bold tracking-tight text-eifo-primary md:text-5xl lg:text-6xl">
            {t('title')}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600 lg:mx-0">
            {t('subtitle')}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="bg-eifo-orange text-white transition-transform hover:scale-105 hover:bg-eifo-orange/90"
            >
              <Link href="/courses">{t('browseCourses')}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="transition-transform hover:scale-105">
              <Link href="/contact">{t('contactUs')}</Link>
            </Button>
          </div>
        </div>

        {/* Photo cluster — desktop only (crowds the CTA on mobile).
            The blob and the floating grad-cap card drift; the student stays put,
            anchored to the bottom of the section. */}
        <div className="relative hidden h-[34rem] self-end lg:block">
          {/* Floating blob backdrop, gently drifting */}
          <Image
            src="/images/blob-orange.png"
            alt=""
            aria-hidden
            width={1000}
            height={907}
            priority
            className="animate-eifo-float-slow absolute inset-x-0 bottom-8 m-auto w-[105%] max-w-none opacity-90"
          />

          {/* Student — static, enlarged, anchored to the bottom (cut off at the
              split). overflow-hidden on the section clips the bottom edge. */}
          <Image
            src="/images/student-hero.png"
            alt={t('studentAlt')}
            width={1000}
            height={982}
            priority
            className="absolute inset-x-0 bottom-0 m-auto w-[92%] max-w-none drop-shadow-xl"
          />

          {/* Floating tablet showing the student Rewards screen — hovers above
              the student's outstretched hand and drifts independently. Painted
              last so it floats in front, resting over her open palm. */}
          <HeroAppMockup className="animate-eifo-float absolute -right-8 top-16 -rotate-6 xl:-right-6" />
        </div>
      </div>
    </section>
  );
}
